<script lang="ts">
    import type {User} from 'typings/portaro.be.types';
    import type {UserDetailView} from '../../types';
    import {ContactType} from 'typings/portaro.be.types';
    import {exists} from 'shared/utils/custom-utils';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpUserAccountPageService} from '../kp-user-account-page.service';
    import KpDropdownMenuButton from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuButton.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpDropdownMenuItem from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuItem.svelte';
    import KpButtonStyleAnchor from 'shared/ui-widgets/button/KpButtonStyleAnchor.svelte';

    export let user: User;
    export let detailView: UserDetailView;
    export let fromSutorRecordDetail = false;

    const localize = getLocalization();
    const service = getInjector().getByClass(KpUserAccountPageService);

    $: hasSmsNumber = exists((user.contacts ?? []).filter((contact) => contact.type.id === ContactType.SMS_PHONE)[0]);
</script>

<!-- Change password -->
{#if !fromSutorRecordDetail && detailView.canUserPasswordChange}
    <KpButton isBlock
              on:click={() => service.showPasswordChangeForm(user)}>
        {localize(/* @kp-localization konto.zmenaHesla */ 'konto.zmenaHesla')}
    </KpButton>
{/if}

<!-- Delete credentials -->
{#if !fromSutorRecordDetail && detailView.canUserCredentialsDelete}
    <KpButton isBlock
              dataQa="delete-user-credentials-button"
              on:click={() => service.deleteCredentials(user)}>

        {localize(/* @kp-localization ctenar.SmazatUzivatelskeJmenoAHeslo */ 'ctenar.SmazatUzivatelskeJmenoAHeslo')}
    </KpButton>
{/if}

<!-- Delete linking with MojeID -->
{#if !fromSutorRecordDetail && detailView.canUserMojeIDLinkDelete}
    <KpButton isBlock
              dataQa="delete-user-link-with-mojeid-button"
              on:click={() => service.deleteLinkWithMojeID(user)}>
        Smazat propojení s MojeID
    </KpButton>
{/if}

<!-- Delete linking with BankID -->
{#if !fromSutorRecordDetail && detailView.canUserBankIDLinkDelete}
    <KpButton isBlock
              dataQa="delete-user-link-with-bankid-button"
              on:click={() => service.deleteLinkWithBankID(user)}>
        Smazat propojení s BankID
    </KpButton>
{/if}

<!-- Edit user -->
{#if detailView.canUserEdit && detailView.userEditationShowing}
    <KpButton isBlock
              dataQa="edit-user-button"
              isDisabled={user.deleted}
              on:click={() => service.editUser(user)}>

        {localize(/* @kp-localization commons.editovat */ 'commons.editovat')}
    </KpButton>
{/if}

<!-- Delete user -->
{#if !fromSutorRecordDetail && detailView.canUserRegistrationPeriodExtend}
    <KpButton isBlock
              dataQa="delete-user-button"
              isDisabled={user.deleted}
              on:click={() => service.deleteUser(user)}>

        {localize(/* @kp-localization commons.Smazat */ 'commons.Smazat')}
    </KpButton>
{/if}

<!-- Print -->
{#if !fromSutorRecordDetail && (detailView.canUserRegistrationAgreementPrint || detailView.canPaymentsReceiptPrint)}
    <KpDropdownMenuButton isBlock
                          additionalContainerClasses="print-btn-group">
        <svelte:fragment slot="button">
            <IconedContent trailingIcon="angle-small-down">
                {localize(/* @kp-localization commons.tisk */ 'commons.tisk')}
            </IconedContent>
        </svelte:fragment>

        <svelte:fragment slot="menu">
            {#if detailView.canUserRegistrationAgreementPrint && detailView.showUserRegistrationAgreementPrintButton}
                <KpDropdownMenuItem dataQa="print-reader-registration-button"
                                    on:click={() => service.printRegistrationAgreement(user)}>
                    {localize(/* @kp-localization konto.RegistrationAgreement */ 'konto.RegistrationAgreement')}
                </KpDropdownMenuItem>
            {/if}

            {#if detailView.canLoanConfirmationPrint && detailView.showUserLoanConfirmationPrintButton}
                <KpDropdownMenuItem dataQa="print-reader-loan-confirmation-button"
                                    on:click={() => service.printLoanConfirmation(user)}>
                    {localize(/* @kp-localization user.LoanConfirmation */ 'user.LoanConfirmation')}
                </KpDropdownMenuItem>
            {/if}

            {#if detailView.canPaymentsReceiptPrint && detailView.showUserReceiptOfPaymentPrintButton}
                <KpDropdownMenuItem dataQa="print-payment-receipt-button"
                                    on:click={() => service.printReceiptOfPayment(user)}>
                    {localize(/* @kp-localization konto.ReceiptOfPayment */ 'konto.ReceiptOfPayment')}
                </KpDropdownMenuItem>
            {/if}
        </svelte:fragment>
    </KpDropdownMenuButton>
{/if}

<!-- User preferences -->
{#if !fromSutorRecordDetail && detailView.canUserPreferencesEdit}
    <KpButton isBlock
              additionalClasses="show-user-preferences-btn"
              dataQa="extend-registration-button"
              isDisabled={user.deleted}
              on:click={() => service.userPreferences(user)}>

        {localize(/* @kp-localization commons.UzivatelskeNastaveni */ 'commons.UzivatelskeNastaveni')}
    </KpButton>
{/if}

<!-- Send E-mail -->
{#if !fromSutorRecordDetail && detailView.canSendEmailToUser && exists(user.email)}
    <KpButton isBlock
              on:click={() => service.sendEmail(user)}>
        {localize(/* @kp-localization mail.SendEmail */ 'mail.SendEmail')}
    </KpButton>
{/if}

<!-- Send SMS -->
{#if !fromSutorRecordDetail && detailView.canSendSmsToUser && hasSmsNumber}
    <KpButton isBlock
              on:click={() => service.sendSms(user)}>
        {localize(/* @kp-localization sms.SendSms */ 'sms.SendSms')}
    </KpButton>
{/if}

<!-- Impersonate user -->
{#if detailView.canAuthSwitchActiveUser}
    <KpButtonStyleAnchor isBlock href="/login/impersonate?user={user.id}">
        Přihlásit se za uživatele
    </KpButtonStyleAnchor>
{/if}

{#if !fromSutorRecordDetail && detailView.showUserDiscountRequestButton}
    <KpButton isBlock
              on:click={() => service.discountRequest(user)}>
        {localize(/* @kp-localization discount.request.Title */ 'discount.request.Title')}
    </KpButton>
{/if}